import { useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../components/Text";
import { useReduceDisclosure } from "../../../../../../../../../../hooks/use-reduce-disclosure";
import { usePatientAllergyList } from "../../../../../../../../../../queries/allergies/use-patient-allergies-list";
// Archive hooks delete the record from the patient's chart, so they must not be
// used to remove an item from a visit note. Kept for reference.
// import { useArchiveAllergy } from "../../../../../../../../../../queries/allergies/use-patient-allergy-archive";
// import { useArchivePatientFamilyHistory } from "../../../../../../../../../../queries/history/past-family/use-patient-family-history-archive";
// import { useArchivePatientMedicineHistory } from "../../../../../../../../../../queries/history/past-medication/use-patient-medication-history-archive";
// import { useArchivePatientSurgicalHistory } from "../../../../../../../../../../queries/history/past-surgical/use-past-surgical-history-archive";
// import { useArchiveMedication } from "../../../../../../../../../../queries/medication/use-patient-medication-archive";
import { usePastFamilyHistoryList } from "../../../../../../../../../../queries/history/past-family/use-patient-family-history-list";
import { usePastMedicalHistoryList } from "../../../../../../../../../../queries/history/past-medication/use-patient-medical-history-list";
import { usePastSurgicalHistoryList } from "../../../../../../../../../../queries/history/past-surgical/use-past-surgical-history-list";
import { usePatientMedicationList } from "../../../../../../../../../../queries/medication/use-patient-medication-list";
import { useRemoveClinicalFromEncounter } from "../../../../../../../../../../queries/encounter/use-remove-clinical-from-encounter";
import { RemoveClinicalDataFromEncounterTypeEnum } from "../../../../../../../../../../services";
import {
  DEF_PAGINATION,
  getXtenantId,
} from "../../../../../../../../../../utils/utils";
import {
  ALLERGY_SECTION,
  buildEntryLine,
  FAMILY_HISTORY_SECTION,
  MEDICAL_HISTORY_SECTION,
  MEDICATION_SECTION,
  SURGICAL_HISTORY_SECTION,
} from "../../../../../../../../../../utils/subjective-entry";
import { StyleAddButton } from "../../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import UpsertAllergiesDrawer from "../../../../allergy/UpsertAllergiesDrawer";
import { convertFamilyHistoryPrefillPayload } from "../../../../clinical-details/components/ClinicalLayout-constant";
import UpsertFamilyHistory from "../../../../history/UpsertFamilyHistory";
import UpsertMedicalHistoryDrawer from "../../../../history/UpsertMedicalHistoryDrawer";
import UpsertSurgicalHistoryDrawer from "../../../../history/UpsertSurgicalHistoryDrawer";
import UpsertMedicationsDrawer from "../../../../medication/UpsertMedicationsDrawer";
import PreviewContentWrapper from "./PreviewContentWrapper";

const PrimaryCareSoapNoteSubjective = () => {
  const [
    allergyDisclosure,
    medicalHistoryDisclosure,
    surgicalHistoryDisclosure,
    familyHistoryDisclosure,
    medicationDisclosure,
  ] = useReduceDisclosure(5);

  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { mutateAsync: removeClinicalFromEncounter } =
    useRemoveClinicalFromEncounter();
  const { data: allergies, refetch: refetchAllergy } = usePatientAllergyList({
    pagination: DEF_PAGINATION,
    appointmentId: appointmentid ?? undefined,
    archive: false,
    status: true,
    xTENANTID: getXtenantId(),
  });

  /**
   * Unlinks the record from this encounter only — the record stays in the
   * patient's chart.
   */
  function removeFromNoteHandler<T>(
    type: RemoveClinicalDataFromEncounterTypeEnum,
    id: T,
    refetchList: () => void
  ) {
    return removeClinicalFromEncounter({
      type,
      appointmentUuid: appointmentid ?? "",
      recordUuid: id?.toString() ?? "",
      xTENANTID: getXtenantId(),
    }).then((res) => {
      refetchList();
      return res;
    });
  }

  const { data: medicalHistories, refetch: referchMedicalHistory } =
    usePastMedicalHistoryList({
      // patientUuid: patientId,
      appointmentUuid: appointmentid ?? undefined,
      pagination: DEF_PAGINATION,
      archive: false,
      xTENANTID: getXtenantId(),
    });
  const { data: surgicalHistory, refetch: refreshSurgicalHistory } =
    usePastSurgicalHistoryList({
      appointmentUuid: appointmentid ?? undefined,
      // patientUuid: patientId,
      pagination: DEF_PAGINATION,
      archive: false,
      xTENANTID: getXtenantId(),
    });
  const { data: familyHistory, refetch: referchFamilyHistory } =
    usePastFamilyHistoryList({
      appointmentUuid: appointmentid ?? undefined,
      // patientUuid: patientId,
      pagination: DEF_PAGINATION,
      archive: false,
      xTENANTID: getXtenantId(),
    });
  const { data: medications, refetch: refetchMedications } =
    usePatientMedicationList({
      archive: false,
      // patientUuid: patientId,
      pagination: DEF_PAGINATION,
      appointmentUuid: appointmentid ?? undefined,
    });

  const filteredMedicalHistory = medicalHistories?.data?.content?.filter(
    (d: any) => !d.archive
  );
  const filteredSurgicalHistory = surgicalHistory?.data?.content?.filter(
    (d: any) => !d.archive
  );
  const filteredFamilyHistory = familyHistory?.data?.content?.filter(
    (d: any) => !d.archive
  );
  const filteredMedications = medications?.data?.content?.filter(
    (d: any) => !d.archive
  );
  return (
    <>
      {!!allergies?.data?.content?.length && (
        <PreviewContentWrapper
          dataList={allergies?.data?.content}
          k="allergy"
          title="Allergies"
          renderContent={(allergy: any) => (
            <Text sx={{ color: "#424242" }}>
              {buildEntryLine(ALLERGY_SECTION, allergy)}
            </Text>
          )}
          onDelete={(uuid) => {
            removeFromNoteHandler(
              RemoveClinicalDataFromEncounterTypeEnum.Allergy,
              uuid,
              refetchAllergy
            );
          }}
          onEdit={(allergy) => {
            allergyDisclosure.open(allergy);
          }}
        />
      )}
      {!!filteredMedicalHistory?.length && (
        <PreviewContentWrapper
          dataList={filteredMedicalHistory}
          k="medicalHistory"
          title="Medical History"
          renderContent={(medicalHistory: any) => (
            <Text sx={{ color: "#424242" }}>
              {buildEntryLine(MEDICAL_HISTORY_SECTION, medicalHistory)}
            </Text>
          )}
          onDelete={(uuid) => {
            removeFromNoteHandler(
              RemoveClinicalDataFromEncounterTypeEnum.MedicalHistory,
              uuid,
              referchMedicalHistory
            );
          }}
          onEdit={(medicalHistory) => {
            medicalHistoryDisclosure.open(medicalHistory);
          }}
        />
      )}
      {!!filteredSurgicalHistory?.length && (
        <PreviewContentWrapper
          dataList={filteredSurgicalHistory}
          k="surgicalHistory"
          title="Surgical History"
          renderContent={(surgicalHistory: any) => (
            <Text sx={{ color: "#424242" }}>
              {buildEntryLine(SURGICAL_HISTORY_SECTION, surgicalHistory)}
            </Text>
          )}
          onDelete={(uuid) => {
            removeFromNoteHandler(
              RemoveClinicalDataFromEncounterTypeEnum.SurgicalHistory,
              uuid,
              refreshSurgicalHistory
            );
          }}
          onEdit={(surgicalHistory) => {
            surgicalHistoryDisclosure.open(surgicalHistory);
          }}
        />
      )}
      {!!filteredFamilyHistory?.length && (
        <PreviewContentWrapper
          dataList={filteredFamilyHistory}
          k="familyHistory"
          title="Family History"
          renderContent={(familyHistory: any) => (
            <Text sx={{ color: "#424242" }}>
              {buildEntryLine(FAMILY_HISTORY_SECTION, familyHistory)}
            </Text>
          )}
          onDelete={(uuid) => {
            removeFromNoteHandler(
              RemoveClinicalDataFromEncounterTypeEnum.FamilyHistory,
              uuid,
              referchFamilyHistory
            );
          }}
          onEdit={(familyHistory) => {
            familyHistoryDisclosure.open(
              convertFamilyHistoryPrefillPayload(familyHistory)
            );
          }}
        />
      )}
      {!!filteredMedications?.length && (
        <PreviewContentWrapper
          dataList={filteredMedications}
          k="medication"
          title="Medications"
          renderContent={(medication: any) => (
            <Text sx={{ color: "#424242" }}>
              {buildEntryLine(MEDICATION_SECTION, medication)}
            </Text>
          )}
          onDelete={(uuid) => {
            removeFromNoteHandler(
              RemoveClinicalDataFromEncounterTypeEnum.Medication,
              uuid,
              refetchMedications
            );
          }}
          onEdit={(medication) => {
            medicationDisclosure.open({
              ...medication,
              medicalCodes: medication?.medicalCodes
                ? Object.keys(medication?.medicalCodes)[0]
                : undefined,
              prescribedBy: medication?.prescribedBy
                ? {
                    value: Object.keys(medication?.prescribedBy)[0],
                    label: Object.values(medication?.prescribedBy)[0],
                  }
                : undefined,
            });
          }}
        />
      )}
      {/* Action Buttons */}
      {/* note: need to replace this button with mapper for all the buttons */}
      <Flex gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <StyleAddButton
          w="120px"
          onClick={() => allergyDisclosure.open()}
          label="Add Allergy"
        />
        <StyleAddButton
          w="100px"
          onClick={() => medicalHistoryDisclosure.open()}
          label="Add PMH"
        />
        <StyleAddButton
          w="100px"
          onClick={() => surgicalHistoryDisclosure.open()}
          label="Add PSH"
        />
        <StyleAddButton
          w="100px"
          onClick={() => familyHistoryDisclosure.open()}
          label="Add FH"
        />
        <StyleAddButton
          w="120px"
          onClick={() => {
            medicationDisclosure.open();
          }}
          label="Add Current Meds"
        />
      </Flex>

      <UpsertAllergiesDrawer disclosureHost={allergyDisclosure} />
      <UpsertMedicalHistoryDrawer disclosureHost={medicalHistoryDisclosure} />
      <UpsertSurgicalHistoryDrawer disclosureHost={surgicalHistoryDisclosure} />
      <UpsertFamilyHistory disclosureHost={familyHistoryDisclosure} />
      <UpsertMedicationsDrawer disclosureHost={medicationDisclosure} />
    </>
  );
};

export default PrimaryCareSoapNoteSubjective;
